import React, { useMemo } from 'react'
import {
  ChartOptionElement,
  GetReportDetailsQuery,
  GetReportFavoriteDetailsQuery,
  ReportElement as GqlReportElement,
} from 'gqlTypes'
import { ChartOptionsContextProvider } from 'contexts/ChartOptionsContext'
import ReportElementText from './ReportElementText'
import ReportElementTable from './ReportElementTable'
import ReportElementBarChart from './ReportElementBarChart'
import ReportElementBoxAndWhiskerChart from './ReportElementBoxAndWhiskerChart'
import ReportElementOther from './ReportElementOther'
import ReportElementScatterChart from './ReportElementScatterChart'
import ReportElementPercentileChart from './ReportElementPercentileChart'
import ReportElementKeyRatingsChart from './ReportElementKeyRatingsChart'
import { Box } from '@mui/material'

export interface ReportElementProps {
  reportId: string
  report:
    | NonNullable<GetReportDetailsQuery['report']>
    | NonNullable<GetReportFavoriteDetailsQuery['reportWithFavorites']>
  element: GqlReportElement
  sectionId: string
  favorites?: boolean
  print?: boolean
  onUpdateFavorites: () => void
}

const ReportElement: React.FC<ReportElementProps> = ({
  reportId,
  report,
  element,
  sectionId,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const elementContent = useMemo(() => {
    if (element.contentType === 'TextElement')
      return (
        <ReportElementText
          {...{
            reportId,
            element,
            report,
            favorites,
            sectionId,
            print,
            onUpdateFavorites,
          }}
        />
      )
    if (element.contentType === 'Table')
      return (
        <ReportElementTable
          {...{
            reportId,
            element,
            report,
            favorites,
            sectionId,
            print,
            onUpdateFavorites,
          }}
        />
      )
    if (element.contentType === 'BarChart')
      return (
        <ReportElementBarChart
          {...{
            reportId,
            element,
            report,
            favorites,
            sectionId,
            print,
            onUpdateFavorites,
          }}
        />
      )
    if (element.contentType === 'BoxAndWhiskerChart')
      return (
        <ReportElementBoxAndWhiskerChart
          {...{
            reportId,
            element,
            report,
            favorites,
            sectionId,
            print,
            onUpdateFavorites,
          }}
        />
      )
    if (element.contentType === 'ScatterChart')
      return (
        <ReportElementScatterChart
          {...{
            reportId,
            element,
            report,
            favorites,
            sectionId,
            print,
            onUpdateFavorites,
          }}
        />
      )
    if (element.contentType === 'PercentileChart')
      return (
        <ReportElementPercentileChart
          {...{
            reportId,
            element,
            report,
            favorites,
            sectionId,
            print,
            onUpdateFavorites,
          }}
        />
      )
    if (element.contentType === 'KeyMeasuresChart')
      return (
        <ReportElementKeyRatingsChart
          {...{
            reportId,
            element,
            report,
            favorites,
            sectionId,
            print,
            onUpdateFavorites,
          }}
        />
      )
    return (
      <ReportElementOther
        {...{
          reportId,
          element,
          report,
          favorites,
          sectionId,
          print,
          onUpdateFavorites,
        }}
      />
    )
  }, [element])

  return elementContent
}

const WrappedReportElement: typeof ReportElement = (props) => (
  <ChartOptionsContextProvider
    id={props.element.id}
    type={ChartOptionElement.Element}
    reportId={props.reportId}
  >
    <Box style={{ width: '100%' }}>
      <ReportElement {...props} />
    </Box>
  </ChartOptionsContextProvider>
)

export default WrappedReportElement
